import * as React from 'react';

import { DocumentType } from '@rematter/frontend-lib-api-client';
import { Checkbox, ContentCard } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';

import { useCheckboxes } from './hooks/useCheckboxes';
import { SalesOrderTabDocumentsTableHeader } from './SalesOrderTabDocumentsTableHeader';
import { SalesOrderTabDocumentsTableRow } from './SalesOrderTabDocumentsTableRow';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { LoadRecord } from '~/modules/trade/types/load';

interface Props {
  bookingId: string;
  loads: (Pick<
    Partial<LoadRecord>,
    'loadNumber' | 'bookingNumber' | 'invoiceId'
  > & {
    id: string;
  })[];
  selectedDocuments: Record<string, Record<DocumentType, boolean>>;
  onSelectedDocumentsChange: (
    selection: Record<string, Record<DocumentType, boolean>>,
  ) => void;
  bookingInvoiceSelected?: boolean;
  onBookingInvoiceSelect?: (checked: boolean) => void;
  bookingNumber?: string;
}

export const SalesOrderTabDocumentsBookingSection: React.FC<Props> = ({
  bookingId,
  loads,
  selectedDocuments,
  onSelectedDocumentsChange,
  bookingInvoiceSelected = false,
  onBookingInvoiceSelect,
  bookingNumber: bookingNumberProp,
}) => {
  const t = useTranslations();

  // Cast loads to compatible type for the checkbox handlers
  const compatibleLoads = React.useMemo(
    () =>
      loads.map((load) => ({
        ...load,
        id: load.id as string,
      })),
    [loads],
  );

  const { handleLoadSelect, handleDocumentSelect, handleHeaderSelect } =
    useCheckboxes(compatibleLoads);

  const getBookingSectionTitle = () => {
    if (bookingId === 'unassigned') {
      return t.salesOrderDetail.documents.labels.unassignedLoads;
    }

    const bookingNumber =
      bookingNumberProp ??
      (compatibleLoads.length === 0
        ? undefined
        : compatibleLoads?.[0]?.bookingNumber);

    if (bookingNumber) {
      return t.salesOrderDetail.documents.labels.booking(bookingNumber);
    }

    return t.salesOrderDetail.documents.labels.bookingWithId(bookingId);
  };

  return (
    <ContentCard className="@SalesOrderTabDocumentsBookingSection mb-6 w-full">
      {/* Booking Section Header */}
      <div className="mb-4 flex items-center gap-4">
        <h3 className="text-lg font-semibold text-neutral">
          {getBookingSectionTitle()}
        </h3>
        {bookingId !== 'unassigned' && onBookingInvoiceSelect && (
          <div
            className={cn(
              'flex h-[45px] min-w-[140px] cursor-pointer items-center gap-2 rounded-md border p-3',
              bookingInvoiceSelected
                ? 'border-[2px] border-primary/80 shadow-[0_0_0_2px_var(--color-primary/20)]'
                : 'border-neutral/10',
            )}
            onClick={() => onBookingInvoiceSelect(!bookingInvoiceSelected)}
          >
            <Checkbox
              isSelected={bookingInvoiceSelected}
              className="pointer-events-none"
            >
              <div className="text-base text-neutral/80">
                {t.salesOrderDetail.documents.types.bookingInvoice}
              </div>
            </Checkbox>
          </div>
        )}
      </div>

      {/* Documents Table */}
      <div className="w-full">
        <SalesOrderTabDocumentsTableHeader
          selectedDocuments={selectedDocuments}
          loads={compatibleLoads}
          onHeaderSelect={(type, checked) =>
            onSelectedDocumentsChange(
              handleHeaderSelect(type, checked)(selectedDocuments),
            )
          }
        />
        {compatibleLoads.map((load) => (
          <SalesOrderTabDocumentsTableRow
            key={load.id}
            load={load}
            selectedDocuments={selectedDocuments}
            onLoadSelect={(loadId, checked) =>
              onSelectedDocumentsChange(
                handleLoadSelect(loadId, checked)(selectedDocuments),
              )
            }
            onDocumentSelect={(loadId, type, checked) =>
              onSelectedDocumentsChange(
                handleDocumentSelect(loadId, type, checked)(selectedDocuments),
              )
            }
          />
        ))}
      </div>
    </ContentCard>
  );
};
